import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { mapKeys, camelCase } from 'lodash';
import type { User } from '@/api/users.ts';
import type { FeatureFlags } from '@/api/feature_flags.ts';
import { emptyFeatureFlags } from '@/api/feature_flags.ts';
import RailsUtility from '../utilities/rails.ts';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import type { UserEntity } from '@/api/entity_types/User.ts';
import { useUserCalendarStore } from '@/stores/userCalendar.ts';
import { toast } from 'vue-sonner';
import { Api } from '@/api/index.ts';
import type { AgentCurrentResponse } from '@/api/agent/current.ts';

const emptyCurrentUser: CurrentUser = {
  id: null,
  timezone: '',
  firstName: '',
  lastName: '',
  eventBuffer: '',
  teamSlug: '',
  postmarkEmailVerified: false,
  emailSignature: '',
  unconfirmedEmail: '',
  email: '',
};

export type CurrentUser = {
  id: number | null;
  timezone: string;
  firstName: string;
  lastName: string;
  eventBuffer: string;
  teamSlug: string;
  postmarkEmailVerified: boolean;
  emailSignature: string;
  unconfirmedEmail: string;
  email: string;
};

const emptyAgentDetails: AgentCurrentResponse = {
  record_type: 'Candidate',
  pipeline_review_type: 'sync',
};

const emptyCurrentUserAccountDetails: CurrentUserAccountDetails = {
  username: '',
  unreadMessages: 0,
  incompleteTasks: 0,
  locked: false,
  role: null,
  logo: '',
  isImpersonated: false,
  featureFlags: emptyFeatureFlags,
  onboardingPath: '',
  isOnboardingCompleted: false,
  useOnboardingInviteFlow: false,
  teamSlug: '',
};

export type CurrentUserAccountDetails = {
  username: string;
  unreadMessages: number;
  incompleteTasks: number;
  locked: boolean;
  role: 'Admin' | 'Agent' | 'Contributor' | null;
  logo: string;
  isImpersonated: boolean;
  featureFlags?: FeatureFlags;
  onboardingPath: string;
  isOnboardingCompleted: boolean;
  useOnboardingInviteFlow: boolean;
  teamSlug?: string;
  teamPhoneNumber?: string;
};

export const useCurrentUserStore = defineStore('currentUser', () => {
  const currentUser = ref<CurrentUser>(emptyCurrentUser);
  const currentUserAccountDetails = ref<CurrentUserAccountDetails>(emptyCurrentUserAccountDetails);
  const currentAgent = ref<AgentCurrentResponse>(emptyAgentDetails);
  const icaAgreementSessionId = ref<string | null>(null);
  const hasLoadedInitialAccountDetails = ref(false);
  const hasLoadedInitialUser = ref(false);
  const hasLoadedInitialAgent = ref(false);
  const loadingSpinnerStore = useLoadingSpinnerStore();

  const setCurrentUser = (payload: {
    currentUser: UserEntity;
    timezone: string;
    teamSlug: string;
    eventBuffer: string;
  }) => {
    currentUser.value = mapKeys(payload.currentUser, (v: any, k: string) => camelCase(k));
    currentUser.value.timezone = payload.timezone;
    currentUser.value.teamSlug = payload.teamSlug;
    currentUser.value.eventBuffer = RailsUtility.convertFromEnumFormat(payload.eventBuffer);
  };

  const setIcaAgreementSessionId = (payload: { icaAgreementSessionId: string | null }) => {
    icaAgreementSessionId.value = payload.icaAgreementSessionId;
  };

  const updateUser = async (payload: { user: UserEntity; updatedUserAttributes: User }) => {
    await Api.Users.updateUser(
      { id: payload.user.id.toString() },
      { updatedUserAttributes: payload.updatedUserAttributes }
    );
    // if user is not set in state, do so
    if (payload.user.id === currentUser.value.id) await loadCurrentUser();
  };

  const loadCurrentUserAccountDetails = async () => {
    const data = await Api.Users.getUserDetails();
    hasLoadedInitialAccountDetails.value = true;
    if (!data) return;

    setCurrentUserAccountDetails({
      details: {
        username: data.username,
        unreadMessages: data.unread_count,
        incompleteTasks: data.incomplete_tasks,
        locked: data.locked,
        role: data.role,
        logo: data.logo,
        isImpersonated: data.impersonated,
        featureFlags: data.feature_flags,
        onboardingPath: data.onboarding_path,
        isOnboardingCompleted: data.onboarding_completed,
        useOnboardingInviteFlow: data.use_onboarding_invite_flow,
        teamSlug: data.team_slug,
        teamPhoneNumber: data.team_phone_number,
      },
    });
  };

  const setCurrentUserAccountDetails = (payload: { details: CurrentUserAccountDetails }) => {
    currentUserAccountDetails.value = payload.details;
  };

  const loadCurrentUser = async () => {
    const userCalendarStore = useUserCalendarStore();
    loadingSpinnerStore.showLocalLoadingSpinner();
    const data = await Api.Users.getUser();
    hasLoadedInitialUser.value = true;

    //@ts-ignore
    if (!data || data.error) {
      loadingSpinnerStore.hideLocalLoadingSpinner();
      return;
    }

    setIcaAgreementSessionId({
      icaAgreementSessionId: data.ica_agreement_session_id,
    });

    setCurrentUser({
      currentUser: data.record,
      // extract timezone, teamSlug, and eventBuffer now, not nested within data.record
      timezone: data.user_calendar?.timezone ?? 'Pacific Time (US & Canada)',
      teamSlug: data.team_slug,
      eventBuffer: data.user_calendar?.event_buffer ?? '',
    });

    userCalendarStore.setCurrentUserAvailability({
      availability: data.user_calendar_availability,
    });

    userCalendarStore.setCurrentUserNotificationSettings({
      notificationSettings: data.record,
    });

    await loadCurrentUserAccountDetails();
    if (data.record?.id) {
      userCalendarStore.loadCalendarAuthStatus({ id: data.record.id.toString() }).catch(() => {});
    }
    loadingSpinnerStore.hideLocalLoadingSpinner();
  };

  const registerUser = async (payload: { registrationData: any }) => {
    loadingSpinnerStore.showLoadingSpinner();
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

      const response = await fetch('/registrations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken || '',
        },
        body: JSON.stringify({
          invite_token: payload.registrationData.invite_token,
          team_name: payload.registrationData.team_name,
          email: payload.registrationData.email,
          password: payload.registrationData.password,
        }),
      });

      if (response.ok) {
        await Promise.all([loadCurrentUser(), loadCurrentUserAccountDetails()]);
        toast.success('Success', {
          action: { label: 'Dismiss' },
          description: 'Registration successful!',
        });

        return { success: true };
      } else {
        const data = await response.json();
        let errorMessage = data.message || 'Registration failed';

        if (response.status === 500) {
          errorMessage = 'A server error occurred. Please try again or contact support.';
        }

        toast.error('Registration Failed', {
          action: { label: 'Dismiss' },
          description: errorMessage,
        });

        return { success: false, error: errorMessage };
      }
    } catch (error: any) {
      let errorMessage = 'An unexpected error occurred during registration';
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      toast.error('Registration Error', {
        action: { label: 'Dismiss' },
        description: errorMessage,
      });

      return { success: false, error: errorMessage };
    } finally {
      loadingSpinnerStore.hideLoadingSpinner();
    }
  };

  const isInitialized = computed(() => {
    if (!hasLoadedInitialAccountDetails.value) return false;
    if (isAgent()) return hasLoadedInitialAgent.value;
    return hasLoadedInitialUser.value;
  });

  const loadCurrentAgent = async () => {
    const response = await Api.AgentCurrent.getCurrent();
    hasLoadedInitialAgent.value = true;
    if (response.status === 'success') {
      currentAgent.value = response.data;
    } else {
      toast.error('Failed to load agent details');
    }
  };

  const initialize = async () => {
    await loadCurrentUserAccountDetails();
    if (isAgent()) {
      await loadCurrentAgent();
    } else {
      await loadCurrentUser();
    }
  };

  const isInternalAdmin = () => !!currentUserAccountDetails.value.featureFlags?.internal_administrator;
  const isAgent = () => currentUserAccountDetails.value.role === 'Agent';
  const isAdmin = () => currentUserAccountDetails.value.role === 'Admin';
  const isContributor = () => currentUserAccountDetails.value.role === 'Contributor';
  const isImpersonating = () => currentUserAccountDetails.value.isImpersonated;

  return {
    currentUser,
    currentUserAccountDetails,
    currentAgent,
    icaAgreementSessionId,
    loadCurrentUser,
    loadCurrentUserAccountDetails,
    loadCurrentAgent,
    setCurrentUser,
    updateUser,
    registerUser,
    isAgent,
    isAdmin,
    isContributor,
    isImpersonating,
    isInternalAdmin,
    initialize,
    isInitialized,
  };
});
